import { useEffect, useState } from "react";
import { fetchCars } from "../api/cars";
import whatsappIcon from "../assets/icons/whatsapp.svg";
import NavBar from "../components/navigation";
import Footer from "../components/Footer";

// Update these to your real contact details.
const CONTACT = {
  whatsapp: "254700000000",
  phone: "+254 700 000 000",
};

const DRIVER_OPTION_LABELS = {
  "Self-drive": "Self-drive",
  "Chauffeur-driven": "Chauffeur-driven",
  Either: "Self-drive or chauffeur",
};

const AVAILABILITY_STYLES = {
  available: { label: "Available now", className: "bg-green-50 text-green-700" },
  hired: { label: "Currently hired", className: "bg-amber-50 text-amber-700" },
  maintenance: { label: "Under maintenance", className: "bg-gray-100 text-gray-500" },
};

// ── Icons ──────────────────────────────────────────────────────

function ChatIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M21 11.5a8.38 8.38 0 0 1-4.5 7.5 8.5 8.5 0 0 1-8.9-.4L3 21l1.9-4.6a8.38 8.38 0 0 1-.9-3.9 8.5 8.5 0 0 1 8.5-8.5h.5a8.48 8.48 0 0 1 8 8v.5z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6.5 3h3l2 5-2.5 1.5a12 12 0 0 0 5.5 5.5L16 12.5l5 2v3a2 2 0 0 1-2 2A16 16 0 0 1 4.5 5a2 2 0 0 1 2-2z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── Small pieces ──────────────────────────────────────────────

function AvailabilityBadge({ status }) {
  const style = AVAILABILITY_STYLES[status] || AVAILABILITY_STYLES.available;
  return <span className={`rounded-full px-3 py-1 text-xs font-semibold ${style.className}`}>{style.label}</span>;
}

function SpecItem({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-sm font-medium text-gray-700">{value}</p>
    </div>
  );
}

function whatsappHref(car) {
  const message = `Hi, I'd like to hire the ${car.make} ${car.model} (${car.year}).`;
  return `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(message)}`;
}

function HireCarCard({ car }) {
  const cover = car.images?.[0];

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="aspect-[4/3] w-full bg-gray-100">
        {cover ? (
          <img src={cover} alt={`${car.make} ${car.model}`} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-400">No photo yet</div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-gray-900">{car.make} {car.model} {car.year}</h3>
          <AvailabilityBadge status={car.availability} />
        </div>

        <div className="mt-4 grid grid-cols-3 gap-3">
          <SpecItem label="Transmission" value={car.transmission} />
          <SpecItem label="Seats" value={car.seats} />
          <SpecItem label="Driver" value={DRIVER_OPTION_LABELS[car.driver_option] || car.driver_option} />
        </div>

        <div className="mt-4 flex items-baseline gap-1">
          <span className="text-xl font-bold text-gray-900">
            {car.currency} {Number(car.price || 0).toLocaleString()}
          </span>
          <span className="text-sm text-gray-400">/ day</span>
        </div>

        {car.min_hire_days && (
          <p className="mt-1 text-xs text-gray-400">{car.min_hire_days}-day minimum hire</p>
        )}

        <div className="mt-auto flex gap-2 pt-4">
          <a
            href={whatsappHref(car)}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-green-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-green-700"
          >
            <img src={whatsappIcon} alt="WhatsApp" className="h-5 w-5" />
            WhatsApp
          </a>
          <a
            href={`tel:${CONTACT.phone}`}
            aria-label="Call to hire"
            className="flex items-center justify-center rounded-lg bg-gray-100 px-3 py-2 text-gray-600 transition-colors hover:bg-gray-200"
          >
            <PhoneIcon />
          </a>
        </div>
      </div>
    </div>
  );
}

// ── Results area ───────────────────────────────────────────────

function CarHireResults({ status, cars }) {
  if (status === "loading") {
    return <p className="mt-16 text-center text-gray-400">Loading available cars...</p>;
  }
  if (status === "error") {
    return (
      <p className="mt-16 text-center text-red-600">
        Couldn't load hire cars right now — please refresh, or contact us directly on WhatsApp.
      </p>
    );
  }
  if (cars.length === 0) {
    return <p className="mt-16 text-center text-gray-400">No cars are listed for hire just yet — check back soon.</p>;
  }
  return (
    <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {cars.map((car) => (
        <HireCarCard key={car._id || car.id} car={car} />
      ))}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────x prisma

function extractHireCars(data) {
  const list = Array.isArray(data) ? data : data.cars || [];
  return list.filter((car) => car.type === "hire");
}

export default function CarHire() {
  const [cars, setCars] = useState([]);
  const [filters, setFilters] = useState({ seats: "", bodyType: "", fuelConsumption: "" });
  const [status, setStatus] = useState("loading"); // loading | ready | error

  useEffect(() => {
    let cancelled = false;

    fetchCars({ type: "hire" })
      .then((data) => {
        if (cancelled) return;
        setCars(extractHireCars(data));
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const filteredCars = cars.filter((car) => {
    const seatsMatch = !filters.seats || Number(car.seats) >= Number(filters.seats);
    const typeMatch = !filters.bodyType || car.bodyType === filters.bodyType;
    const consumptionMatch = !filters.fuelConsumption || Number(car.fuelConsumption) >= Number(filters.fuelConsumption);
    return seatsMatch && typeMatch && consumptionMatch;
  });

  return (
    <>
    <NavBar/>
    <div className="min-h-screen bg-gray-50">
      <main className="mx-auto max-w-7xl px-4 py-12 md:px-10">
        <h1 className="text-center text-3xl font-bold text-gray-900">Cars Available for Hire</h1>
        <p className="mt-2 text-center text-gray-500">Self-drive and chauffeur-driven options — contact us to book.</p>
        <section className="mx-auto mt-8 max-w-4xl rounded-2xl border border-gray-200 bg-white p-5">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-semibold text-gray-900">Filter hire cars</h2>
            {(filters.seats || filters.bodyType || filters.fuelConsumption) && <button type="button" onClick={() => setFilters({ seats: "", bodyType: "", fuelConsumption: "" })} className="text-sm font-semibold text-[#c1392a]">Clear</button>}
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <label className="grid gap-2 text-sm font-medium text-gray-700">Minimum seats<input type="number" min="1" value={filters.seats} onChange={(e) => setFilters({ ...filters, seats: e.target.value })} placeholder="Any" className="rounded-lg border border-gray-200 px-3 py-2" /></label>
            <label className="grid gap-2 text-sm font-medium text-gray-700">Type of car<select value={filters.bodyType} onChange={(e) => setFilters({ ...filters, bodyType: e.target.value })} className="rounded-lg border border-gray-200 px-3 py-2"><option value="">Any type</option>{[...new Set(cars.map((car) => car.bodyType).filter(Boolean))].sort().map((type) => <option key={type} value={type}>{type}</option>)}</select></label>
            <label className="grid gap-2 text-sm font-medium text-gray-700">Minimum fuel consumption (L/100 km)<input type="number" min="0" step="0.1" value={filters.fuelConsumption} onChange={(e) => setFilters({ ...filters, fuelConsumption: e.target.value })} placeholder="Any" className="rounded-lg border border-gray-200 px-3 py-2" /></label>
          </div>
        </section>
        <CarHireResults status={status} cars={filteredCars} />
      </main>
    </div>
    <Footer/>

  </>
  );
}